<template>
    <div>
        <Component
            :is="globalStore.header.component"
            v-if="globalStore && globalStore.header"
            :global="globalStore.global.content"
            :blok="globalStore.header"
        />
        <main>
            <Component
                :is="story.content.component"
                v-if="story"
                :blok="story.content"
                :products="products"
                :airport-stats="products.airportStats"
            />
        </main>
        <Component
            :is="globalStore.footer.component"
            v-if="globalStore && globalStore.footer"
            :global="globalStore.global.content"
            :blok="globalStore.footer"
        />
    </div>
</template>

<script setup lang="ts">
import { useGlobalStore } from '@nuxt-monorepo/base/app/store/global';
import type { IAirportProducts, IAirportEntity } from '@nuxt-monorepo/ui/types';
import { getPortParkingSummary, getProductsFromResponse } from '~/services/apiProductService';
import type { ComputedRef } from 'vue';
import type { IParkingSummary, IParkingSummaryInfo } from '@nuxt-monorepo/ui/types';

// Constant variable for access to quote store
const globalStore: any = useGlobalStore();
const { locale } = useI18n();
const route: any = useRoute();

const { STORYBLOK_FOLDER, STORYBLOK_VERSION, STORYBLOK_ROUTE_BLOCKED }: any = useRuntimeConfig().public;

// Need to resolve shared components to allow the storyblok API
// to expose this for us to use.
const resolveRelations: string[] = [
    'page.globals',
    'CoreSharedComponent.SharedComponent',
    'APRAirportPage.globals',
    'APRAirportPage.AirportEntity',
    'PSFAirportPage.globals',
    'PSFAirportPage.AirportEntity',
    'PartnersContainer.Partners',
];

const sbLocation = `/${STORYBLOK_FOLDER}/pages/v2/airports/parking/${route.params.IATA}`;

const isInVisualEditor = !!route?.query?.['_storyblok_tk[token]']; //Check if frontend is opened in visual editor

// Perform request to Storyblok to retrieve content
const data: any = await useAsyncStoryblok(sbLocation, {
    // Enable deep reactivity to fix live editing when in visual editor.
    // Because of changes for useAsyncData in Nuxt4 https://github.com/storyblok/monoblok/issues/210#issuecomment-3172675601
    deep: isInVisualEditor,
    api: {
        version: STORYBLOK_VERSION === 'published' ? 'published' : 'draft',
        language: locale.value,
        resolve_relations: resolveRelations,
    },
    bridge: {
        resolveRelations: resolveRelations,
    },
});

const story = data?.story;
const error = data?.error;

if (story) {
    let config: { STORYBLOK_VERSION: string; STORYBLOK_FOLDER: string } = {
        STORYBLOK_VERSION: STORYBLOK_VERSION,
        STORYBLOK_FOLDER: STORYBLOK_FOLDER,
    };
    setSeoMeta(story.value?.content?.seo, config, route);
    if (story.value?.content?.globals) {
        globalStore.update(story.value.content.globals);
    }
}

function getStoryblokBlockedRoutes() {
    if (STORYBLOK_ROUTE_BLOCKED) {
        const blocked = STORYBLOK_ROUTE_BLOCKED.split(', ');
        return blocked.some((block) => route.path.includes(block));
    } else {
        return false;
    }
}

// Send user to error page if no story found
if (getStoryblokBlockedRoutes()) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}

if (error?.value) {
    throw createError({
        statusCode: error.value.statusCode ?? 500,
        statusMessage: error.value.statusMessage ?? 'Failed to load page',
        fatal: true,
    });
}

onMounted(() => {
    // Allow use of Storyblok Bridge (Visual Editor)
    useStoryblokBridge(story.id, (evStory: any) => (story.value = evStory), {
        resolveRelations,
    });
});

const airport: IAirportEntity = story.value.content.AirportEntity.content;
const apiEntity: any = (story.value.content.api_entity || [])[0];
const slug =
    apiEntity?.og_slug ??
    `${airport.city?.replaceAll(' ', '-').toLowerCase()}-airport-parking-${airport.code?.toLowerCase()}`;
const slugUrl = apiEntity?.og_slug_url
    ? `https://airportparkingreservations.com/${apiEntity?.og_slug_url}`
    : `https://airportparkingreservations.com/${airport.city?.replaceAll(' ', '-').toLowerCase()}-airport-parking-${airport.code?.toLowerCase()}`;

const apr: any = await useAsyncData(
    `${airport.code?.toLowerCase()}/airport-parking`,
    async () => {
        if (apiEntity?.ugc) {
            const ugc_content: any = await $privateApi(`ugc_content/parking/${airport.code?.toLowerCase()}`, {
                method: 'GET',
            });
            const ugc = { data: { providers: { Apr: {}, Psf: {} } } };
            ugc.data.providers.Apr = ugc_content?.data?.cards;
            ugc.data.providers.Psf = ugc_content?.data?.psfLots;
            return ugc;
        } else {
            return await $privateApi('contents/pages', {
                method: 'POST',
                body: {
                    domain: 'apr.com',
                    subdomain: `${airport.code?.toLowerCase()}`,
                    slug: slug,
                    full_url: slugUrl,
                },
            });
        }
    },
    { lazy: true }
);

const { t } = useI18n();
const products: IAirportProducts = getProductsFromResponse(apr);
const parkingSummary: IParkingSummary = !apiEntity?.ugc ? getPortParkingSummary(apr) : null;
const summaryInfo: ComputedRef<IParkingSummaryInfo> = computed(() => {
    if (!apiEntity?.ugc) return;
    return {
        title: `Parking Reservations - Today’s Key Info for ${parkingSummary.code}`,
        data: [
            {
                label: `${t('numberOfLots')}:`,

                value: `${parkingSummary.totalLots} ${t('parkingLots')}`,
            },
            {
                label: `${t('bestRated')}:`,
                value: parkingSummary.bestRatedLot,
            },
            {
                label: `${t('mostBooked')}:`,
                value: parkingSummary.mostBookedLot,
            },
            {
                label: `${t('closestToAirport')}:`,
                value: parkingSummary.closestToAirportLot,
            },
        ],
    };
});
provide('parkingSummary', summaryInfo);
provide('airportData', airport);
provide('apiProducts', products);
</script>
